import React, { useState, useEffect } from "react";
import {
  MdCalendarToday,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdFileDownload,
  MdRefresh,
  MdBarChart
} from "react-icons/md";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { BASE_URL } from "../../../utils/constant";
import GlobalPDFReport from "./GlobalPDFReport";
import { PDFDownloadLink } from "@react-pdf/renderer";

const GlobalReport = ({ organizationId }) => {
  const [startDate, setStartDate] = useState(format(subMonths(new Date(), 1), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedSections, setSelectedSections] = useState([
    "material_request",
    "po",
    "rfq",
    "design",
    "work_orders",
    "vendor_advance_payment",
    "contractor_workorder_payment"
  ]);

  const sections = [
    { id: "material_request", label: "Material Request", description: "All material requests and line items" },
    { id: "po", label: "Purchase Orders", description: "Purchase orders with payment status" },
    { id: "rfq", label: "RFQs", description: "Requests for Quotation and status" },
    { id: "design", label: "Design", description: "Project design documents and status" },
    { id: "work_orders", label: "Work Orders", description: "Active work orders and values" },
    { id: "vendor_advance_payment", label: "Vendor Advance", description: "Advance payments to vendors" },
    { id: "contractor_workorder_payment", label: "Contractor Payments", description: "Payments made to contractors" },
  ];

  const datePresets = [
    { label: "Last 7 Days", getValue: () => ({ start: format(subDays(new Date(), 7), "yyyy-MM-dd"), end: format(new Date(), "yyyy-MM-dd") }) },
    { label: "Last 30 Days", getValue: () => ({ start: format(subDays(new Date(), 30), "yyyy-MM-dd"), end: format(new Date(), "yyyy-MM-dd") }) },
    { label: "This Month", getValue: () => ({ start: format(startOfMonth(new Date()), "yyyy-MM-dd"), end: format(endOfMonth(new Date()), "yyyy-MM-dd") }) },
    { label: "Last Month", getValue: () => ({ start: format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"), end: format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd") }) },
  ];

  const toggleSection = (id) => {
    if (selectedSections.includes(id)) {
      setSelectedSections(selectedSections.filter(s => s !== id));
    } else {
      setSelectedSections([...selectedSections, id]);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);
    try {
      const accessToken = localStorage.getItem("access");
      const org = JSON.parse(localStorage.getItem("organization"));
      const finalOrgId = organizationId || org?.id;

      if (!finalOrgId) throw new Error("Organization ID not found");

      const response = await fetch(`${BASE_URL}/org/organization/${finalOrgId}/global-report/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          sections: selectedSections
        })
      });

      const result = await response.json();
      if (result.status) {
        setReportData(result.data);
      } else {
        alert(result.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("An error occurred while generating the report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 p-8 bg-[#F5F7FA] overflow-y-auto h-full">
      <div className="mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#3B0069]">Global Report</h1>
            <p className="text-gray-500 mt-1">Generate comprehensive organization-wide insights and reports</p>
          </div>
          <button
            onClick={() => {
              setReportData(null);
              setStartDate(format(subMonths(new Date(), 1), "yyyy-MM-dd"));
              setEndDate(format(new Date(), "yyyy-MM-dd"));
              setSelectedSections(sections.map(s => s.id));
            }}
            className="flex items-center gap-2 px-4 py-2 text-[#3B0069] bg-white rounded-lg border border-[#3B0069] hover:bg-[#F5F3FF] transition-all"
          >
            <MdRefresh size={20} />
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Configuration Column */}
          <div className="md:col-span-1 space-y-6">
            {/* Date Selection */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MdCalendarToday className="text-[#3B0069]" />
                Date Range
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {datePresets.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        const { start, end } = preset.getValue();
                        setStartDate(start);
                        setEndDate(end);
                      }}
                      className="text-xs py-2 px-1 rounded-md border border-gray-200 hover:border-[#3B0069] hover:text-[#3B0069] transition-all"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1 font-medium">START DATE</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3B0069]/20 focus:border-[#3B0069]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1 font-medium">END DATE</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3B0069]/20 focus:border-[#3B0069]"
                  />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleGenerateReport}
              disabled={loading || selectedSections.length === 0}
              className={`w-full py-4 rounded-2xl font-bold text-white shadow-lg transition-all transform active:scale-95 ${loading || selectedSections.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#3B0069] hover:bg-[#2A004D] hover:shadow-[#3B0069]/30"
                }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Generating...
                </div>
              ) : (
                "Generate Preview"
              )}
            </button>
          </div>

          {/* Sections Column */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 h-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Select Report Sections</h2>
                <div className="flex gap-4">
                  <button
                    onClick={() => setSelectedSections(sections.map(s => s.id))}
                    className="text-xs text-[#3B0069] font-medium hover:underline"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedSections([])}
                    className="text-xs text-gray-500 font-medium hover:underline"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {sections.map((section) => (
                  <div
                    key={section.id}
                    onClick={() => toggleSection(section.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedSections.includes(section.id)
                        ? "border-[#3B0069] bg-[#F5F3FF]"
                        : "border-gray-100 hover:border-gray-200"
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      {selectedSections.includes(section.id) ? (
                        <MdCheckBox className="text-[#3B0069] mt-0.5" size={20} />
                      ) : (
                        <MdCheckBoxOutlineBlank className="text-gray-300 mt-0.5" size={20} />
                      )}
                      <div>
                        <p className={`font-semibold text-sm ${selectedSections.includes(section.id) ? "text-[#3B0069]" : "text-gray-700"}`}>
                          {section.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {reportData && (
          <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-[#3B0069] rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <MdBarChart size={120} />
              </div>
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <h2 className="text-2xl font-bold">Report Ready!</h2>
                    <p className="text-purple-200 mt-1">Generated for {reportData.organization.name} from {startDate} to {endDate}</p>

                    <div className="flex flex-wrap gap-4 mt-6">
                      {Object.entries(reportData.section_counts).map(([key, count]) => (
                        <div key={key} className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg">
                          <p className="text-[10px] uppercase tracking-wider text-purple-200">{key.replace(/_/g, " ")}</p>
                          <p className="text-lg font-bold">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <PDFDownloadLink
                      document={<GlobalPDFReport data={reportData} />}
                      fileName={`Global_Report_${reportData.organization.name}_${format(new Date(), "yyyyMMdd")}.pdf`}
                    >
                      {({ loading: pdfLoading }) => (
                        <button
                          disabled={pdfLoading}
                          className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#3B0069] rounded-xl font-bold shadow-lg hover:bg-purple-50 transition-all text-lg"
                        >
                          {pdfLoading ? "Preparing..." : (
                            <>
                              <MdFileDownload size={24} />
                              Download PDF Report
                            </>
                          )}
                        </button>
                      )}
                    </PDFDownloadLink>
                    <p className="text-center text-xs text-purple-300">Fast, secure PDF generation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalReport;
